import { useRequiredContext } from "@redotech/react-util/context";
import { useLoad } from "@redotech/react-util/load";
import { useHasPermission } from "@redotech/redo-merchant-app-common/auth/permissions-context";
import { RedoMerchantClientContext } from "@redotech/redo-merchant-app-common/client/context";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { getTeamWebhooks } from "@redotech/redo-merchant-general-app/client/team-webhook";
import { WebhookStatus } from "@redotech/redo-merchant-general-app/setting/developer/types";
import { GeneralPermissions } from "@redotech/redo-model/permissions";
import { NotificationBannerItem } from "@redotech/redo-web/arbiter-components/notification/redo-notification-banner";
import { useMemo } from "react";
import { ActionRequiredSeverity } from "./action-required-notification";

export function useFixDeveloperWebhooks(): {
  severity: ActionRequiredSeverity;
  item: NotificationBannerItem;
} | null {
  const client = useRequiredContext(RedoMerchantClientContext);
  const team = useRequiredContext(TeamContext);

  const canManageTeamDevelopment = useHasPermission(
    GeneralPermissions.MANAGE_TEAM_DEVELOPMENT,
  );

  const webhooksLoad = useLoad(
    async (signal: AbortSignal) => {
      if (!canManageTeamDevelopment) {
        return [];
      }

      return await getTeamWebhooks(client, { signal });
    },
    [client],
  );

  const hasDisabledWebhooks = useMemo(() => {
    if (webhooksLoad.pending || webhooksLoad.error || !webhooksLoad.value) {
      return false;
    }

    const disabledWebhooks = webhooksLoad.value.filter(
      (webhook) => webhook.status === WebhookStatus.DISABLED,
    );

    return disabledWebhooks.length > 0;
  }, [webhooksLoad]);

  if (!hasDisabledWebhooks) {
    return null;
  }

  return {
    severity: ActionRequiredSeverity.Low,
    item: {
      id: "fix-developer-webhooks",
      type: "warning",
      message:
        "Some of your webhook notifications have been disabled because of high failure rates. Please fix them in the developer settings.",
      actions: [
        {
          actionType: "link",
          href: `/stores/${team._id}/settings/developer`,
          text: "Fix webhooks",
          internal: true,
        },
      ],
    },
  };
}
